import { fetchAPI, safeFetchAPI } from '@/api';
import { extractNestedErrors, getPoliticalAgencyBilling, log, userUtils, utils } from '@/helpers';
import { Setter } from '@/legacy-types';
import { AccountSettingsProps } from '@/pages/settings/account/AccountSettings';
import { getDefaultSignupForm } from '@/pages/signup-form-builder/signup-builder';
import { CampaignSettingsSchema, IndustrySchema, ProfileSchema, UserSchema, UserSettingsSchema } from '@/schemas';
import { FullPartial, TimezoneOptions, User, UserSettings } from '@/types';
import { cloneDeep, isEqual, merge, values } from 'lodash';
import { z } from 'zod';
import { create } from 'zustand';
import { useSignupFormsStore } from '../signup-forms/useSignupFormsStore';
import { useSignupBuilderStore } from '../signup-forms/useSignupBuilderStore';

export type AccountInfoStoreState = {
	loaded: boolean;
	loading: boolean;

	isCreatingUser: boolean;

	errors: Record<string, string>;

	ogUser: FullPartial<User> | undefined;
	user: FullPartial<User> | undefined;

	ogSettings: FullPartial<UserSettings> | undefined;
	settings: FullPartial<UserSettings> | undefined;
};

export type AccountInfoStoreActions = {
	initialize: (newUser: AccountSettingsProps) => Promise<void>;

	setUser: (user: Setter<FullPartial<User>>) => void;
	setSettings: (settings: Setter<FullPartial<UserSettings>>) => void;

	hasChanged: () => boolean;
	validate: () => Record<string, string>;
	handleSubmit: () => Promise<boolean>;
	reset: () => void;

	upsertUser: () => Promise<boolean>;
	upsertSettings: () => Promise<boolean>;
};

export type AccountInfoStore = AccountInfoStoreState & AccountInfoStoreActions;

const initialState: AccountInfoStoreState = {
	loaded: false,
	loading: false,

	ogUser: undefined,
	user: undefined,

	ogSettings: undefined,
	settings: undefined,

	isCreatingUser: false,

	errors: {},
};

export const useAccountSettingsStore = create<AccountInfoStore>((set, get) => ({
	...initialState,
	initialize: async ({ creatingUser, agencyID }: AccountSettingsProps) => {
		let user = {} as User;
		let settings = {} as UserSettings;

		if (creatingUser) {
			user.type = agencyID ? 'advertiser' : 'agency';
			user.agencyID = agencyID;
			user.utmTags = true;
		} else {
			const [currUser, userError] = await safeFetchAPI<User>('/users/:uid');
			const [currSettings, _] = await safeFetchAPI<UserSettings>('/user/settings/:uid');
			if (userError) {
				utils?.toaster?.addToast?.({
					message: userError || 'Error getting user',
					type: 'danger',
				});
				return;
			}

			user = currUser ?? ({} as User);
			settings = currSettings ?? ({} as UserSettings);
		}

		// Special Agencies get the parent user's email and industry by default
		if (!!agencyID && user?.id !== agencyID && utils.isSpecialAgency()) {
			const [parent, _parentError] = await safeFetchAPI<User>(`/users/${agencyID}`);

			user.agencyID = agencyID;
			user.profile.email = parent?.profile?.email ?? '';
			user.profile.industry = parent?.profile?.industry ?? (utils.isDispojoy() ? 'cannabis' : 'political');
		}

		set({
			loaded: true,
			loading: false,
			isCreatingUser: creatingUser,
			user,
			ogUser: cloneDeep(user),
			settings,
			ogSettings: cloneDeep(settings),
		});
	},
	setUser: (user: Setter<FullPartial<User>>) => {
		const currentUser = get().user;
		const newUser = typeof user === 'function' ? user(currentUser ?? {}) : user;
		set({ user: newUser });
	},
	setSettings: (settings: Setter<FullPartial<UserSettings>>) => {
		const currentSettings = get().settings;
		const newSettings = typeof settings === 'function' ? settings(currentSettings ?? {}) : settings;
		set({ settings: newSettings });
	},
	hasChanged: () => {
		if (!get().user || !get().settings) {
			return false;
		}

		return !isEqual(get().user, get().ogUser) || !isEqual(get().settings, get().ogSettings);
	},

	validate: () => {
		const errors: Record<string, string> = {};

		const requiredProfileFields = ProfileSchema.pick({
			name: true,
			email: true,
			phone: true,
			timezone: true,
			industry: true,
			website: true,
		}).required();

		const userSchema = UserSchema.pick({
			profile: true,
			subType: get().user?.type !== 'agency' ? true : undefined,
			type: true,
		}).extend({
			profile: requiredProfileFields,
		});
		const userResult = userSchema.safeParse(get().user ?? {});
		if (!userResult.success) {
			Object.assign(errors, extractNestedErrors(userResult.error, ''));
		}

		const settingsSchema = UserSettingsSchema.pick({
			campaignSettings: true,
		});
		const settingsResult = settingsSchema.safeParse(get().settings ?? {});
		if (!settingsResult.success) {
			Object.assign(errors, extractNestedErrors(settingsResult.error, ''));
		}

		// If there are no changes, we don't need to save anything
		if (isEqual(get().user, get().ogUser) && isEqual(get().settings, get().ogSettings)) {
			return {
				message: 'No changes to save',
			};
		}

		log('--------------------------------');
		log('userResult', userResult);
		log('settingsResult', settingsResult);
		log('--------------------------------');
		log('errors', errors);
		log('--------------------------------');

		return errors;
	},

	handleSubmit: async () => {
		const errors = get().validate();
		if (Object.keys(errors).length > 0) {
			set({ errors });

			// Get the first error message
			const errorMessage = Object.values(errors)[0];

			utils?.toaster?.addToast?.({
				message: errorMessage,
				type: 'danger',
			});
			return false;
		}

		const { isCreatingUser } = get();
		const { user, ogUser } = get();
		const { settings, ogSettings } = get();

		set({ loading: true });

		if (!isEqual(user, ogUser) || isCreatingUser) {
			const success = await get().upsertUser();
			if (!success) {
				set({ loading: false });
				return false;
			}
		}

		if (!isEqual(settings, ogSettings)) {
			await get().upsertSettings();
		}

		utils?.toaster?.addToast?.({
			message: `${isCreatingUser ? 'User created' : 'User updated'} successfully`,
			type: 'success',
		});
		set({ loading: false });
		get().reset();
		return true;
	},
	upsertUser: async () => {
		const { isCreatingUser } = get();
		const { user, ogUser } = get();

		if (isCreatingUser) {
			const newUserReq: FullPartial<User> = {
				...user,
				message2FA: user?.message2FA ?? true,
				utmTags: user?.utmTags ?? true,
				optOutBounces: user?.optOutBounces ?? true,
				mmsLogo: utils.isPolitical() ? false : true,
				allowBanned: user?.allowBanned ?? true,
			};

			const [createUserResp, createUserError] = await safeFetchAPI<{ uid: string }>(utils.isAdmin() ? '/p/signup' : '/advertiser/:uid', {
				method: 'POST',
				payload: { user: newUserReq },
			});

			const userID = createUserResp?.uid ?? '';

			if (createUserError) {
				utils?.toaster?.addToast?.({
					message: createUserError || 'Error creating user',
					type: 'danger',
				});
				set({ loading: false });
				return false;
			}

			// For special political agencies, we copy the billing from their parent agency to the advertiser
			if (utils.isSpecialAgency()) {
				const billingObj = getPoliticalAgencyBilling({
					uid: userID,
					agencyID: newUserReq?.agencyID ?? '',
					name: newUserReq?.profile?.name ?? '',
				});
				if (billingObj) {
					await fetchAPI(`/api/v1/users/fees/${userID}`, {
						method: 'PUT',
						payload: billingObj,
					});
				}
			}
		}

		// If we are not creating a user, we need to update the user
		// Get the existing user object so we can make sure we're not overwriting any other changes
		// Then we'll merge the new changes into the object & send it to the server
		if (!isCreatingUser) {
			const [currUser = {}, _userError] = await safeFetchAPI<User>('/users/:uid');

			if (_userError) {
				utils?.toaster?.addToast?.({
					message: _userError || 'Error getting user',
					type: 'danger',
				});
				set({ loading: false });
				return false;
			}

			const payload = merge(currUser, user);

			const [_, editUserError] = await safeFetchAPI(`/users/${ogUser?.type === 'agency' ? user?.id : user?.agencyID}/${user?.id}`, {
				method: 'PUT',
				payload,
			});

			if (editUserError) {
				utils?.toaster?.addToast?.({
					message: editUserError || 'Error updating user',
					type: 'danger',
				});
				set({ loading: false });
				return false;
			}
		}

		// If dispense org id has changed
		if (userUtils.isAdmin() && user?.dispenseOrgID !== ogUser?.dispenseOrgID) {
			const [_dispenseID, _dispenseIDError] = await safeFetchAPI(`/users/dispenseID/:uid?orgID=${user?.dispenseOrgID}`, {
				method: 'POST',
			});

			if (_dispenseIDError) {
				utils?.toaster?.addToast?.({
					message: _dispenseIDError || 'Error setting dispense org id',
					type: 'danger',
				});
			}
		}

		if (user?.brandStoresForMarketAnalytics !== ogUser?.brandStoresForMarketAnalytics) {
			const status = user?.brandStoresForMarketAnalytics;
			const [_, updateErr] = await safeFetchAPI(`/users/brandStoresForMA/:uid/${status}`, {
				method: 'PUT',
			});

			if (updateErr) {
				utils?.toaster?.addToast?.({
					message: updateErr || 'Error setting brand stores for market analytics',
					type: 'danger',
				});
			}
		}

		return true;
	},
	upsertSettings: async () => {
		const { settings } = get();

		const [_, editSettingsError] = await safeFetchAPI(`/user/settings/:uid`, {
			method: 'PUT',
			payload: settings,
		});

		if (editSettingsError) {
			utils?.toaster?.addToast?.({
				message: editSettingsError || 'Error updating settings',
				type: 'danger',
			});
			return false;
		}

		return true;
	},
	reset: () => {
		const { isCreatingUser, user } = get();
		const agencyID = user?.agencyID;
		set({ ...initialState });
		get().initialize({ creatingUser: isCreatingUser, agencyID });
	},
}));
